import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react'
import { useCrosswordStore } from '../store'
import type { PreviewPlacement, Word, FittingPair, FittingSingleWord, CellConstraint, Pairing, GridPlacement, ConstructorGrid, PairingStatus, ClueStatus } from '../types'
import { getLettersFromText } from '../lib/pairings'
import { findFittingPairs, findFittingSingleWords } from '../lib/constructor'
import { getCellSize } from '../lib/gridSizing'
import { useCrosswordSettings } from '../contexts/CrosswordSettingsContext'
import { GridDisplay } from './constructor/GridDisplay'
import { ManualEntryModal } from './constructor/ManualEntryModal'
import { ExploreColumn, type ColumnTab } from './ExploreColumn'
import { PairDetailColumn } from './PairDetailColumn'
import { CrossingsPanel } from './CrossingsPanel'
import { AssistantPanel } from './AssistantPanel'
import { CellSuggestionsBody } from './CellSuggestionsBody'
import { StatusFilter } from './StatusFilter'
import { TagFilterRow, DEFAULT_TAG_FILTER_STATE, type TagFilterState } from './TagFilterRow'
import { RatingFilter, DEFAULT_RATING_FILTER_STATE, type RatingFilterState } from './RatingFilter'
import { ConstructorQualityPanel } from './ConstructorQualityPanel'
import { getQualityReport } from '../lib/qualityChecks'

const DEFAULT_GRID_SIZE = 15
type CanvasMode = 'draft' | 'scratchpad'

// Cell-suggestion compute knobs. Previously also lived as toggles in the
// (now-removed) Suggestions sidebar tab; defaults are the only path now,
// kept as state in case we resurface UI later.
type SuggestionRelaxOptions = {
  includeUnpaired: boolean
  showPlaced: boolean
  allowOutOfBounds: boolean
  allowConflicts: boolean
}
const DEFAULT_SUGGESTION_RELAX_OPTIONS: SuggestionRelaxOptions = {
  includeUnpaired: false,
  showPlaced: false,
  allowOutOfBounds: false,
  allowConflicts: false,
}

type ConstructorViewProps = {
  onPairClick?: () => void
}

type CellSuggestionsResult = {
  fittingPairs: { across: FittingPair[]; down: FittingPair[] }
  fittingSingleWords: { across: FittingSingleWord[]; down: FittingSingleWord[] }
  cellConstraints: CellConstraint[]
}

const EMPTY_CELL_SUGGESTIONS: CellSuggestionsResult = {
  fittingPairs: { across: [], down: [] },
  fittingSingleWords: { across: [], down: [] },
  cellConstraints: [],
}

function computeCellSuggestions(
  suggestionGrid: ConstructorGrid | null,
  suggestionSelectedCell: { row: number; col: number } | null,
  words: Word[],
  pairings: Pairing[],
  relax: SuggestionRelaxOptions
): CellSuggestionsResult {
  // Empty grid: every word fits anywhere — solution space is too large to be
  // useful. Suggestions only make sense once the user has placed something.
  if (!suggestionGrid || !suggestionSelectedCell || suggestionGrid.placements.length === 0) {
    return {
      fittingPairs: { across: [], down: [] },
      fittingSingleWords: { across: [], down: [] },
      cellConstraints: [],
    }
  }

  const { row, col } = suggestionSelectedCell

  const existingDirectionsAtCell = new Set<'across' | 'down'>()
  for (const placement of suggestionGrid.placements) {
    const wordLen = placement.wordAText.replace(/\s/g, '').length
    for (let i = 0; i < wordLen; i++) {
      const pRow = placement.direction === 'down' ? placement.row + i : placement.row
      const pCol = placement.direction === 'across' ? placement.col + i : placement.col
      if (pRow === row && pCol === col) {
        existingDirectionsAtCell.add(placement.direction)
        break
      }
    }
  }

  const pairOpts = {
    requireIntersection: suggestionGrid.placements.length > 0,
    includeAllPairs: true,
    includeInflections: false,
    allowOutOfBounds: relax.allowOutOfBounds,
    allowConflictsAwayFromActive: relax.allowConflicts,
    includePlaced: relax.showPlaced,
  }
  const singleOpts = {
    requireIntersection: suggestionGrid.placements.length > 0,
    includeInflections: false,
    allowOutOfBounds: relax.allowOutOfBounds,
    allowConflictsAwayFromActive: relax.allowConflicts,
    includePlaced: relax.showPlaced,
  }

  const acrossPairs = existingDirectionsAtCell.has('across')
    ? []
    : findFittingPairs(suggestionGrid, row, col, 'across', 15, words, pairings, pairOpts)
  const downPairs = existingDirectionsAtCell.has('down')
    ? []
    : findFittingPairs(suggestionGrid, row, col, 'down', 15, words, pairings, pairOpts)

  const acrossSingleWords = existingDirectionsAtCell.has('across')
    ? []
    : findFittingSingleWords(suggestionGrid, row, col, 'across', 15, words, pairings, singleOpts)
  const downSingleWords = existingDirectionsAtCell.has('down')
    ? []
    : findFittingSingleWords(suggestionGrid, row, col, 'down', 15, words, pairings, singleOpts)

  const cellConstraints: CellConstraint[] = []
  for (const direction of ['across', 'down'] as const) {
    if (existingDirectionsAtCell.has(direction)) continue

    const maxLen = direction === 'across' ? suggestionGrid.width - col : suggestionGrid.height - row
    const patternA: (string | null)[] = []
    const patternB: (string | null)[] = []

    for (let i = 0; i < maxLen; i++) {
      const checkRow = direction === 'down' ? row + i : row
      const checkCol = direction === 'across' ? col + i : col
      const cell = suggestionGrid.cells[checkRow]?.[checkCol]
      if (cell?.isBlocked) break
      patternA.push(cell?.letterA ?? null)
      patternB.push(cell?.letterB ?? null)
    }

    const hasConstraint = patternA.some(l => l !== null) || patternB.some(l => l !== null)
    if (hasConstraint || suggestionGrid.placements.length === 0) {
      cellConstraints.push({ direction, patternA, patternB, selectedIndex: 0 })
    }
  }

  return {
    fittingPairs: { across: acrossPairs, down: downPairs },
    fittingSingleWords: { across: acrossSingleWords, down: downSingleWords },
    cellConstraints,
  }
}

/**
 * "Used / unused" filter — two chips matching the same icon-button shape as
 * the status row. Mutually exclusive: clicking 'used' clears 'unused' and
 * vice versa; clicking the active one again returns to 'all' (no filter).
 */
function UsedFilterChip({
  value,
  onChange,
}: {
  value: 'all' | 'used' | 'unused'
  onChange: (next: 'all' | 'used' | 'unused') => void
}) {
  const buttons = [
    { state: 'used' as const, icon: '●', tone: 'bg-blue-100 text-blue-600', title: 'Show only pairs placed in the active draft' },
    { state: 'unused' as const, icon: '○', tone: 'bg-violet-100 text-violet-600', title: 'Show only pairs not placed in the active draft' },
  ]
  return (
    <div className="flex gap-1">
      {buttons.map(({ state, icon, tone, title }) => {
        const isActive = value === state
        return (
          <button
            key={state}
            onClick={() => onChange(isActive ? 'all' : state)}
            title={title}
            className={`w-7 h-7 text-xs rounded-md transition-all flex items-center justify-center ${
              isActive ? tone : 'bg-white text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            }`}
          >
            {icon}
          </button>
        )
      })}
    </div>
  )
}


export function ConstructorView({ onPairClick }: ConstructorViewProps) {
  // Render-cycle timing — logs synchronous render duration and the full
  // mount-to-paint window. Mirrors the pattern in ExploreColumn so you can
  // line up parent / child timings in the console.
  const renderStartRef = useRef(performance.now())
  renderStartRef.current = performance.now()
  const isFirstRenderRef = useRef(true)
  useLayoutEffect(() => {
    const renderMs = performance.now() - renderStartRef.current
    if (isFirstRenderRef.current) {
      console.log(`[perf] ConstructorView first render: ${renderMs.toFixed(1)}ms`)
      isFirstRenderRef.current = false
      const mountStart = renderStartRef.current
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          console.log(`[perf] ConstructorView mount → paint: ${(performance.now() - mountStart).toFixed(1)}ms`)
        })
      })
    } else if (renderMs > 16) {
      console.log(`[perf] ConstructorView re-render: ${renderMs.toFixed(1)}ms`)
    }
  })

  const settings = useCrosswordSettings()
  const gridDisplayRef = useRef<HTMLDivElement>(null)
  const {
    words,
    pairings,
    listNames,
    constructorGrid,
    constructorViewMode,
    setConstructorViewMode,
    selectedGridCell,
    selectedPairingId,
    drafts,
    scratchpadGrid,
    canUndo,
    canRedo,
    undo,
    redo,
    createConstructorGrid,
    placeOnGrid,
    removeFromGrid,
    rotateGridPlacement,
    resizeConstructorGrid,
    toggleGridCellsBlocked,
    setSelectedGridCell,
    saveManualEntry,
    createDraft,
    createScratchpadGrid,
    setDraggedPairingId,
    updateGridPlacementLetter,
    updateScratchpadPlacementLetter,
    saveTempWord,
    setPlacementPriority,
    updatePairingClue,
    setSelectedPairing,
    setSelectedWord,
    placeOnScratchpad,
    removeFromScratchpad,
    removeManyFromScratchpad,
    moveScratchpadPlacements,
    rotateScratchpadPlacement,
    clearScratchpadGrid,
    moveGridPlacements,
    removeManyFromGrid,
    sendPlacementsToScratchpad,
    saveFragment,
  } = useCrosswordStore()

  const [gridWidth, setGridWidth] = useState(constructorGrid?.width ?? DEFAULT_GRID_SIZE)
  const [gridHeight, setGridHeight] = useState(constructorGrid?.height ?? DEFAULT_GRID_SIZE)
  const [showManualEntry, setShowManualEntry] = useState(false)
  const [placementDirection] = useState<'across' | 'down'>('across')
  const [selectedPlacementIds, setSelectedPlacementIds] = useState<ReadonlySet<string>>(new Set())
  const [scratchpadSelectedCell, setScratchpadSelectedCell] = useState<{ row: number; col: number } | null>(null)
  const [scratchpadSelectedPlacementIds, setScratchpadSelectedPlacementIds] = useState<ReadonlySet<string>>(new Set())
  const [canvasMode, setCanvasMode] = useState<CanvasMode>('draft')
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [previewPlacement, setPreviewPlacement] = useState<PreviewPlacement | null>(null)
  // Multi-placement preview (for backwards-search solutions) — a chain of
  // ghosts shown together so the user can see the full hypothetical state.
  const [previewPlacements, setPreviewPlacements] = useState<PreviewPlacement[]>([])
  // Filters lifted from ExploreColumn so PairDetailColumn / backwards search
  // see the same filtering the user applied in the pair list.
  const [pairStatusFilters, setPairStatusFilters] = useState<PairingStatus[]>([])
  const [pairClueFilters, setPairClueFilters] = useState<(ClueStatus | 'empty')[]>([])
  const [pairTagFilters, setPairTagFilters] = useState<TagFilterState>(DEFAULT_TAG_FILTER_STATE)
  const [pairRatingFilter, setPairRatingFilter] = useState<RatingFilterState>(DEFAULT_RATING_FILTER_STATE)
  // Used / unused is the derived axis (placement) — a tristate chip in the
  // context strip. 'all' = no filter; 'used' = placed in any draft;
  // 'unused' = not placed anywhere.
  const [usedFilter, setUsedFilter] = useState<'all' | 'used' | 'unused'>('all')

  // Clear hover preview whenever the grid changes (move/place/remove). Without
  // this, a stale previewPlacement from a previous hover keeps rendering its
  // ghost letters at the old position even after the user has moved on.
  useEffect(() => {
    setPreviewPlacement(null)
    setPreviewPlacements([])
  }, [constructorGrid, scratchpadGrid])
  const [showQualityChecks, setShowQualityChecks] = useState(false)
  const [enforceSymmetry] = useState(false)
  const [highlightedWords, setHighlightedWords] = useState<Set<string>>(new Set())
  const [exploreActiveTab, setExploreActiveTab] = useState<ColumnTab>('pairs')
  const [pairCrossingsOpen, setPairCrossingsOpen] = useState(false)
  // The right-side assistant panel — user-toggleable. Defaults to open so the
  // pair detail (and, in later phases, suggestions) is visible without an
  // extra step. Closing it persists across pair selections.
  const [assistantOpen, setAssistantOpen] = useState(true)
  const selectedPairing = pairings.find(p => p.id === selectedPairingId) ?? null
  const [suggestionRelaxOptions] = useState<SuggestionRelaxOptions>(DEFAULT_SUGGESTION_RELAX_OPTIONS)

  // Suggestion caches: recomputed in the render loop only when their inputs
  // change (grid, words, pairings, selected cell). No effects, no timers — stale
  // is detected by reference-comparing the inputs we last saw.
  const cellSuggestionCacheRef = useRef<{ deps: unknown[]; value: CellSuggestionsResult } | null>(null)


  // Drag state for palette items - tracks placement being dragged from palette
  const [draggingFromPaletteId, setDraggingFromPaletteId] = useState<string | null>(null)
  const [dragStartPos, setDragStartPos] = useState<{ x: number; y: number } | null>(null)

  const qualityReport = useMemo(
    () => getQualityReport(constructorGrid, pairings),
    [constructorGrid, pairings]
  )

  const wordsById = useMemo(
    () => new Map(words.map(word => [word.id, word])),
    [words]
  )


  const pairingsById = useMemo(
    () => new Map(pairings.map(pairing => [pairing.id, pairing])),
    [pairings]
  )

  // Tags currently in play on the active grid — feeds the "no theme repeats"
  // chip. Recomputed when placements or pairings change. Cheap.
  const usedTagsInGrid = useMemo(() => {
    const tags = new Set<string>()
    const grid = canvasMode === 'scratchpad' ? scratchpadGrid : constructorGrid
    if (!grid) return tags
    for (const placement of grid.placements) {
      if (!placement.pairingId) continue
      const p = pairingsById.get(placement.pairingId)
      if (!p?.tags) continue
      for (const t of p.tags) tags.add(t)
    }
    return tags
  }, [canvasMode, constructorGrid, scratchpadGrid, pairingsById])

  // Pairing IDs placed in the active draft (constructorGrid). Drives the
  // used/unused filter chip. Scoped to the active draft only — switching
  // drafts re-derives. Scratchpad placements don't count as "used"; the
  // scratchpad is a freeform staging area, not a committed placement.
  const placedInActiveDraftPairingIds = useMemo(() => {
    const ids = new Set<string>()
    if (!constructorGrid) return ids
    for (const placement of constructorGrid.placements) {
      if (placement.pairingId) ids.add(placement.pairingId)
    }
    return ids
  }, [constructorGrid])

  const hasQualityIssues = qualityReport.totalIssues > 0
  const isScratchpadMode = canvasMode === 'scratchpad'

  // Active canvas aliases — switch suggestion inputs between draft and scratchpad.
  const activeGrid = isScratchpadMode ? scratchpadGrid : constructorGrid
  const activeSelectedCell = isScratchpadMode ? scratchpadSelectedCell : selectedGridCell
  const activeSelectedPlacementIds = isScratchpadMode ? scratchpadSelectedPlacementIds : selectedPlacementIds

  // When the pair panel is open and the user clicks a single placement on the
  // grid, swap the panel to that placement's pairing. Only triggers when
  // there's a real pairingId (manual / temp placements have none).
  useEffect(() => {
    if (!selectedPairingId) return
    if (activeSelectedPlacementIds.size !== 1) return
    const onlyId = activeSelectedPlacementIds.values().next().value as string
    const placement = activeGrid?.placements.find(p => p.id === onlyId)
    if (!placement?.pairingId) return
    if (placement.pairingId === selectedPairingId) return
    setSelectedPairing(placement.pairingId)
  }, [activeSelectedPlacementIds, selectedPairingId, activeGrid, setSelectedPairing])

  const setActiveSelection = (ids: ReadonlySet<string>) => {
    if (isScratchpadMode) setScratchpadSelectedPlacementIds(ids)
    else setSelectedPlacementIds(ids)
  }

  // Single canonical "selection update" entry point — replace by default,
  // toggle when additive (shift / cmd).
  const handleSelectPlacement = (placementId: string | null, additive: boolean) => {
    const current = activeSelectedPlacementIds
    if (placementId === null) {
      if (!additive && current.size > 0) setActiveSelection(new Set())
      return
    }
    if (additive) {
      const next = new Set(current)
      if (next.has(placementId)) next.delete(placementId)
      else next.add(placementId)
      setActiveSelection(next)
    } else if (current.size !== 1 || !current.has(placementId)) {
      setActiveSelection(new Set([placementId]))
    }
  }

  // Scratchpad is now a real fixed-size grid, so its placements live in normal
  // grid coords — suggestion logic uses activeGrid directly with no translation.
  const suggestionGrid = activeGrid
  const suggestionSelectedCell = activeSelectedCell

  const placementContainsCell = (
    placement: GridPlacement,
    row: number,
    col: number
  ) => {
    const wordLen = getLettersFromText(placement.wordAText).length
    for (let i = 0; i < wordLen; i++) {
      const pRow = placement.direction === 'down' ? placement.row + i : placement.row
      const pCol = placement.direction === 'across' ? placement.col + i : placement.col
      if (pRow === row && pCol === col) return true
    }
    return false
  }

  const findPlacementAtCell = (
    row: number,
    col: number,
    grid: ConstructorGrid | null = constructorGrid,
    preferredPlacementIds: ReadonlySet<string> = selectedPlacementIds
  ) => {
    if (!grid) return null

    if (preferredPlacementIds.size > 0) {
      for (const placement of grid.placements) {
        if (preferredPlacementIds.has(placement.id) && placementContainsCell(placement, row, col)) {
          return placement
        }
      }
    }

    return grid.placements.find(p => placementContainsCell(p, row, col)) ?? null
  }

  const handleToggleBlocked = (row: number, col: number) => {
    if (!constructorGrid) return

    const cells: Array<{ row: number; col: number }> = [{ row, col }]
    if (enforceSymmetry) {
      const mirroredRow = constructorGrid.height - 1 - row
      const mirroredCol = constructorGrid.width - 1 - col
      if (mirroredRow !== row || mirroredCol !== col) {
        cells.push({ row: mirroredRow, col: mirroredCol })
      }
    }

    toggleGridCellsBlocked(cells)
  }

  // Cell suggestions are consumed by the AssistantPanel's CellSuggestionsBody —
  // i.e. when the assistant is open, no pair is focused, and a cell is selected.
  // Outside that surface there's no consumer, so skip the compute. The cache
  // holds stale values across closes so reopening with unchanged inputs is
  // instant.
  const cellSuggestionsActive = assistantOpen && selectedPairingId === null && activeSelectedCell !== null

  const cellSuggestionDeps: unknown[] = [
    suggestionGrid,
    suggestionSelectedCell?.row ?? null,
    suggestionSelectedCell?.col ?? null,
    words,
    pairings,
    suggestionRelaxOptions.allowOutOfBounds,
    suggestionRelaxOptions.allowConflicts,
    suggestionRelaxOptions.showPlaced,
  ]
  if (
    cellSuggestionsActive && (
      !cellSuggestionCacheRef.current ||
      cellSuggestionDeps.some((d, i) => d !== cellSuggestionCacheRef.current!.deps[i])
    )
  ) {
    const t0 = performance.now()
    const value = computeCellSuggestions(suggestionGrid, suggestionSelectedCell, words, pairings, suggestionRelaxOptions)
    console.log(`[perf] computeCellSuggestions: ${(performance.now() - t0).toFixed(1)}ms`)
    cellSuggestionCacheRef.current = { deps: cellSuggestionDeps, value }
  }
  const cellSuggestionValue: CellSuggestionsResult = cellSuggestionsActive && cellSuggestionCacheRef.current
    ? cellSuggestionCacheRef.current.value
    : EMPTY_CELL_SUGGESTIONS
  const fittingPairsForCell = cellSuggestionValue.fittingPairs
  const fittingSingleWordsForCell = cellSuggestionValue.fittingSingleWords

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeElement = document.activeElement as HTMLElement | null
      if (activeElement &&
        (activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'SELECT' ||
          activeElement.isContentEditable)) {
        return
      }

      const isMetaKey = e.metaKey || e.ctrlKey
      const keyLower = e.key.toLowerCase()

      if (isMetaKey && keyLower === 'z') {
        e.preventDefault()
        if (e.shiftKey) {
          if (canRedo) redo()
        } else if (canUndo) {
          undo()
        }
        return
      }

      if (isMetaKey && keyLower === 'y') {
        e.preventDefault()
        if (canRedo) redo()
        return
      }

      // Escape to deselect
      if (e.key === 'Escape') {
        e.preventDefault()
        if (isScratchpadMode) {
          setScratchpadSelectedCell(null)
          setScratchpadSelectedPlacementIds(new Set())
        } else {
          setSelectedGridCell(null)
          setSelectedPlacementIds(new Set())
        }
        return
      }

      if (isScratchpadMode) {
        if (!scratchpadGrid) return
        const selectedIds = scratchpadSelectedPlacementIds

        if (keyLower === 'r' && selectedIds.size > 0) {
          e.preventDefault()
          for (const id of selectedIds) rotateScratchpadPlacement(id)
          return
        }

        if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.size > 0) {
          e.preventDefault()
          removeManyFromScratchpad([...selectedIds])
          setScratchpadSelectedPlacementIds(new Set())
          return
        }

        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
          e.preventDefault()
          let deltaRow = 0
          let deltaCol = 0
          if (e.key === 'ArrowUp') deltaRow = -1
          if (e.key === 'ArrowDown') deltaRow = 1
          if (e.key === 'ArrowLeft') deltaCol = -1
          if (e.key === 'ArrowRight') deltaCol = 1

          if (e.shiftKey && selectedIds.size > 0) {
            const updates: Array<{ placementId: string; newRow: number; newCol: number }> = []
            for (const placement of scratchpadGrid.placements) {
              if (!selectedIds.has(placement.id)) continue
              updates.push({
                placementId: placement.id,
                newRow: placement.row + deltaRow,
                newCol: placement.col + deltaCol,
              })
            }
            if (updates.length > 0) moveScratchpadPlacements(updates)
          }

          if (scratchpadSelectedCell) {
            setScratchpadSelectedCell({
              row: scratchpadSelectedCell.row + deltaRow,
              col: scratchpadSelectedCell.col + deltaCol
            })
          } else {
            setScratchpadSelectedCell({ row: deltaRow, col: deltaCol })
          }
          return
        }

        return
      }

      if (!constructorGrid) return
      const selectedIds = selectedPlacementIds

      if (keyLower === 'r' && selectedIds.size > 0) {
        e.preventDefault()
        for (const id of selectedIds) rotateGridPlacement(id)
        return
      }

      if (keyLower === 'b' && selectedGridCell) {
        if (
          selectedGridCell.row >= 0 &&
          selectedGridCell.row < constructorGrid.height &&
          selectedGridCell.col >= 0 &&
          selectedGridCell.col < constructorGrid.width
        ) {
          e.preventDefault()
          handleToggleBlocked(selectedGridCell.row, selectedGridCell.col)
        }
        return
      }

      // Delete/Backspace to remove selected placements (only if not editing a temp letter)
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedIds.size > 0) {
        // If editing a temp word's cell, let GridDisplay handle letter clear instead
        if (selectedIds.size === 1 && selectedGridCell) {
          const onlyId = selectedIds.values().next().value as string
          const placement = constructorGrid.placements.find(p => p.id === onlyId)
          if (placement && (placement.isTempA || placement.isTempB)) {
            return
          }
        }
        e.preventDefault()
        removeManyFromGrid([...selectedIds])
        setSelectedPlacementIds(new Set())
        return
      }

      // Arrow keys to move selected cell
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault()

        let deltaRow = 0
        let deltaCol = 0
        if (e.key === 'ArrowUp') deltaRow = -1
        if (e.key === 'ArrowDown') deltaRow = 1
        if (e.key === 'ArrowLeft') deltaCol = -1
        if (e.key === 'ArrowRight') deltaCol = 1

        // Shift + Arrow moves all selected placements by one cell as a group
        if (e.shiftKey && selectedIds.size > 0) {
          const updates: Array<{ placementId: string; newRow: number; newCol: number }> = []
          for (const placement of constructorGrid.placements) {
            if (!selectedIds.has(placement.id)) continue
            updates.push({
              placementId: placement.id,
              newRow: placement.row + deltaRow,
              newCol: placement.col + deltaCol,
            })
          }
          if (updates.length > 0) moveGridPlacements(updates)
          if (selectedGridCell) {
            setSelectedGridCell({
              row: selectedGridCell.row + deltaRow,
              col: selectedGridCell.col + deltaCol
            })
          }
          return
        }

        // If no cell selected, start at center
        const currentRow = selectedGridCell?.row ?? Math.floor(constructorGrid.height / 2)
        const currentCol = selectedGridCell?.col ?? Math.floor(constructorGrid.width / 2)

        const newRow = Math.max(0, Math.min(constructorGrid.height - 1, currentRow + deltaRow))
        const newCol = Math.max(0, Math.min(constructorGrid.width - 1, currentCol + deltaCol))

        setSelectedGridCell({ row: newRow, col: newCol })

        // Auto-select placement at new cell if one exists
        const placementAtCell = findPlacementAtCell(newRow, newCol, constructorGrid, selectedIds)
        setSelectedPlacementIds(placementAtCell ? new Set([placementAtCell.id]) : new Set())
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    canRedo,
    canUndo,
    constructorGrid,
    moveGridPlacements,
    redo,
    removeManyFromGrid,
    rotateGridPlacement,
    selectedGridCell,
    selectedPlacementIds,
    setSelectedGridCell,
    handleToggleBlocked,
    isScratchpadMode,
    moveScratchpadPlacements,
    removeManyFromScratchpad,
    rotateScratchpadPlacement,
    scratchpadGrid,
    scratchpadSelectedCell,
    scratchpadSelectedPlacementIds,
    undo
  ])

  // Handle palette drag start - create placement at center and let GridDisplay handle the drag
  const handlePaletteDragStart = (pairingId: string, e: React.PointerEvent) => {
    if (!constructorGrid) return

    // Mark drag as in progress to prevent sync during drag
    setDraggedPairingId(pairingId)

    // Create placement at grid center initially - GridDisplay will immediately move it to pointer position
    const targetGrid = isScratchpadMode && scratchpadGrid ? scratchpadGrid : constructorGrid
    const fallbackRow = Math.floor(targetGrid.height / 2)
    const fallbackCol = Math.floor(targetGrid.width / 2)
    const startRow = isScratchpadMode ? scratchpadSelectedCell?.row ?? fallbackRow : fallbackRow
    const startCol = isScratchpadMode ? scratchpadSelectedCell?.col ?? fallbackCol : fallbackCol

    // Create the placement immediately
    const newPlacementId = isScratchpadMode
      ? createScratchpadPlacementFromPairing(pairingId, startRow, startCol)
      : createPlacementFromPairing(pairingId, startRow, startCol)
    if (newPlacementId) {
      setDraggingFromPaletteId(newPlacementId)
      setDragStartPos({ x: e.clientX, y: e.clientY })
      if (isScratchpadMode) {
        setScratchpadSelectedPlacementIds(new Set([newPlacementId]))
      } else {
        setSelectedPlacementIds(new Set([newPlacementId]))
      }
    }
  }

  // Called when GridDisplay finishes the external drag
  const handleExternalDragEnd = () => {
    setDraggingFromPaletteId(null)
    setDragStartPos(null)
    // Clear drag flag to allow sync now that drag is complete
    setDraggedPairingId(null)
  }

  // Helper to create a placement from a pairing
  const createPlacementFromPairing = (pairingId: string, row: number, col: number): string | null => {
    const pairing = pairingsById.get(pairingId)
    if (!pairing) return null

    const wordA = wordsById.get(pairing.wordAId)
    const wordB = wordsById.get(pairing.wordBId)
    if (!wordA || !wordB) return null

    // Create the placement and return its ID
    const result = placeOnGrid({
      pairingId: pairing.id,
      wordAText: wordA.text,
      wordBText: wordB.text,
      row,
      col,
      direction: placementDirection,
    })

    return result.placementId ?? null
  }

  const createScratchpadPlacementFromPairing = (pairingId: string, row: number, col: number): string | null => {
    const pairing = pairingsById.get(pairingId)
    if (!pairing) return null

    const wordA = wordsById.get(pairing.wordAId)
    const wordB = wordsById.get(pairing.wordBId)
    if (!wordA || !wordB) return null

    const result = placeOnScratchpad({
      pairingId: pairing.id,
      wordAText: wordA.text,
      wordBText: wordB.text,
      row,
      col,
      direction: placementDirection,
    })

    return result.placementId ?? null
  }

  // Initialize with a draft if none exist
  useEffect(() => {
    if (drafts.length === 0 && !constructorGrid) {
      createDraft('Untitled')
    }
  }, [drafts.length, constructorGrid, createDraft])

  useEffect(() => {
    if (!scratchpadGrid) {
      createScratchpadGrid()
    }
  }, [scratchpadGrid, createScratchpadGrid])

  // Initialize grid if needed (for backwards compatibility)
  if (!constructorGrid) {
    if (drafts.length === 0) {
      createDraft('Untitled')
    } else {
      createConstructorGrid(DEFAULT_GRID_SIZE, DEFAULT_GRID_SIZE)
    }
    return null
  }

  const handleResizeGrid = () => {
    resizeConstructorGrid(gridWidth, gridHeight)
  }

  const handleClearScratchpadGrid = () => {
    if ((scratchpadGrid?.placements.length ?? 0) > 0 && !window.confirm('Clear scratchpad placements?')) {
      return
    }
    clearScratchpadGrid()
    setScratchpadSelectedCell(null)
    setScratchpadSelectedPlacementIds(new Set())
  }

  // No-op for GridDisplay - palette creates placements directly now
  const handleDropOnCell = (_row: number, _col: number) => { }

  const getActiveCanvasOrigin = (row: number, col: number) => {
    if (isScratchpadMode && scratchpadSelectedCell) {
      return scratchpadSelectedCell
    }
    return { row, col }
  }

  const selectActiveCanvasPlacement = (placementId: string) => {
    // Select the placement (highlights it on the grid) but DON'T move the
    // selected cell — it would steal focus from whatever list the user just
    // clicked from (suggestions / pair panel placements).
    if (isScratchpadMode) setScratchpadSelectedPlacementIds(new Set([placementId]))
    else setSelectedPlacementIds(new Set([placementId]))
  }

  const placePlacementOnActiveCanvas = (placement: Omit<GridPlacement, 'id'>): string | null => {
    const place = isScratchpadMode ? placeOnScratchpad : placeOnGrid
    const result = place(placement)
    if (!result.placementId) return null
    selectActiveCanvasPlacement(result.placementId)
    return result.placementId
  }

  const handleManualEntrySave = (wordAText: string, wordBText: string, clue?: string) => {
    const pairing = saveManualEntry(wordAText, wordBText, clue)
    if (!pairing || !selectedGridCell) return

    placeOnGrid({
      pairingId: pairing.id,
      wordAText,
      wordBText,
      row: selectedGridCell.row,
      col: selectedGridCell.col,
      direction: placementDirection,
    })
  }


  const handleRotatePlacement = (placementId: string) => {
    if (isScratchpadMode) {
      rotateScratchpadPlacement(placementId)
    } else {
      rotateGridPlacement(placementId)
    }
  }

  const handlePlacePairFromSuggestion = (pairingId: string, row: number, col: number, direction: 'across' | 'down') => {
    const pairing = pairingsById.get(pairingId)
    if (!pairing) return

    const wordA = wordsById.get(pairing.wordAId)
    const wordB = wordsById.get(pairing.wordBId)
    if (!wordA || !wordB) return

    const origin = getActiveCanvasOrigin(row, col)
    placePlacementOnActiveCanvas({
      pairingId: pairing.id,
      wordAText: wordA.text,
      wordBText: wordB.text,
      row: origin.row,
      col: origin.col,
      direction,
    })
    setPreviewPlacement(null)
  }

  const handlePlacePairWithWords = (wordAText: string, wordBText: string, row: number, col: number, direction: 'across' | 'down') => {
    const pairing = saveManualEntry(wordAText, wordBText)
    if (!pairing) return

    const origin = getActiveCanvasOrigin(row, col)
    placePlacementOnActiveCanvas({
      pairingId: pairing.id,
      wordAText,
      wordBText,
      row: origin.row,
      col: origin.col,
      direction
    })
    setPreviewPlacement(null)
  }

  const handleDragStateChange = (isDragging: boolean) => {
    // Use a placeholder ID when dragging existing placements
    // The sync code just checks if this is truthy
    setDraggedPairingId(isDragging ? 'dragging' : null)
    // Drop any hover-preview ghosts when a drag starts. Without this, a stale
    // multi-placement preview from a Solutions row keeps rendering its faded
    // letters all over the grid while the user tries to drag a group.
    if (isDragging) {
      setPreviewPlacement(null)
      setPreviewPlacements([])
    }
  }

  // Fit grid to panel with padding
  const handleFitToPanel = () => {
    if (!gridDisplayRef.current || !constructorGrid) return

    const container = gridDisplayRef.current
    const containerWidth = container.clientWidth
    const containerHeight = container.clientHeight

    const cellSize = getCellSize(settings.size)
    const cellWithGap = cellSize + 1

    // Calculate grid dimensions in pixels
    const gridPixelWidth = constructorGrid.width * cellWithGap
    const gridPixelHeight = constructorGrid.height * cellWithGap

    // Add padding (40px on each side)
    const padding = 80
    const availableWidth = containerWidth - padding
    const availableHeight = containerHeight - padding

    // Calculate zoom to fit both dimensions
    const zoomX = availableWidth / gridPixelWidth
    const zoomY = availableHeight / gridPixelHeight
    const fitZoom = Math.min(zoomX, zoomY, 2) // Cap at 2x

    setZoom(Math.round(fitZoom * 100) / 100) // Round to 2 decimal places
    setPan({ x: 0, y: 0 })
  }

  const handlePanChange = (nextPan: { x: number; y: number }) => {
    setPan(prev => (prev.x === nextPan.x && prev.y === nextPan.y ? prev : nextPan))
  }

  // For status bar single-placement detail rendering. Multi-selection paths use IDs directly.
  const onlyScratchpadSelectedId = scratchpadSelectedPlacementIds.size === 1
    ? (scratchpadSelectedPlacementIds.values().next().value as string)
    : null
  const scratchpadSelectedPlacement = onlyScratchpadSelectedId && scratchpadGrid
    ? scratchpadGrid.placements.find(p => p.id === onlyScratchpadSelectedId) ?? null
    : null
  const onlySelectedPlacementId = selectedPlacementIds.size === 1
    ? (selectedPlacementIds.values().next().value as string)
    : null

  return (
    <div className="h-full flex gap-3">
      {/* Left sidebar - ExploreColumn */}
      <div className="w-80 shrink-0">
        <ExploreColumn
          onPairDragStart={handlePaletteDragStart}
          onPairClick={onPairClick}
          selectedGridCell={activeSelectedCell}
          fittingPairs={fittingPairsForCell}
          onPlaceFittingPair={(pair, direction) => {
            if (!activeSelectedCell) return
            const startRow = direction === 'down' ? activeSelectedCell.row - pair.startOffset : activeSelectedCell.row
            const startCol = direction === 'across' ? activeSelectedCell.col - pair.startOffset : activeSelectedCell.col
            // Recorded pair → place via existing pairing record. Unrecorded
            // (no metadata yet) → place by raw word texts; the pairing record
            // is lazily created by downstream actions if/when metadata is set.
            if (pair.pairingId) {
              handlePlacePairFromSuggestion(pair.pairingId, startRow, startCol, direction)
            } else {
              handlePlacePairWithWords(pair.wordAText, pair.wordBText, startRow, startCol, direction)
            }
          }}
          onPreviewFittingPair={(pair, direction) => {
            if (!activeSelectedCell) {
              setPreviewPlacement(null)
              return
            }
            if (!pair) {
              setPreviewPlacement(null)
              return
            }
            const startRow = direction === 'down' ? activeSelectedCell.row - pair.startOffset : activeSelectedCell.row
            const startCol = direction === 'across' ? activeSelectedCell.col - pair.startOffset : activeSelectedCell.col
            setPreviewPlacement({
              wordAText: pair.wordAText,
              wordBText: pair.wordBText,
              row: startRow,
              col: startCol,
              direction
            })
          }}
          onPairHover={(wordTexts) => {
            if (wordTexts) {
              setHighlightedWords(new Set(wordTexts.map(getLettersFromText)))
            } else {
              setHighlightedWords(new Set())
            }
          }}
          activeTab={exploreActiveTab}
          onActiveTabChange={setExploreActiveTab}
          statusFilters={pairStatusFilters}
          onStatusFiltersChange={setPairStatusFilters}
          clueFilters={pairClueFilters}
          onClueFiltersChange={setPairClueFilters}
          tagFilters={pairTagFilters}
          usedTagsInGrid={usedTagsInGrid}
          usedFilter={usedFilter}
          placedPairingIds={placedInActiveDraftPairingIds}
          ratingFilter={pairRatingFilter}
        />
      </div>

      {/* Main area */}
      <div className="relative flex-1 flex flex-col overflow-hidden bg-white rounded-xl shadow-sm">
        {/* Compact toolbar — Drafts and Fragments now live as sidebar tabs. */}
        <div className="flex items-center gap-3 px-3 py-2 border-b border-gray-200">
          <div className="flex gap-0.5 p-0.5 bg-gray-100 rounded">
            {(['draft', 'scratchpad'] as CanvasMode[]).map(mode => (
              <button
                key={mode}
                onClick={() => {
                  setCanvasMode(mode)
                  setPreviewPlacement(null)
                }}
                className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${
                  canvasMode === mode
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
                title={mode === 'draft' ? 'Edit the active printable draft' : 'Freeform canvas that does not count as placed'}
              >
                {mode === 'draft' ? 'Draft' : 'Scratchpad'}
              </button>
            ))}
          </div>

          <div className="w-px h-4 bg-gray-200" />

          {/* Undo / Redo */}
          <div className="flex items-center gap-0.5">
            <button
              onClick={undo}
              disabled={!canUndo}
              className="px-2 py-0.5 text-xs text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded disabled:text-gray-300 disabled:hover:bg-transparent"
              title="Undo (Cmd/Ctrl+Z)"
            >
              Undo
            </button>
            <button
              onClick={redo}
              disabled={!canRedo}
              className="px-2 py-0.5 text-xs text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded disabled:text-gray-300 disabled:hover:bg-transparent"
              title="Redo (Shift+Cmd/Ctrl+Z)"
            >
              Redo
            </button>
          </div>

          <div className="w-px h-4 bg-gray-200" />

          {/* Grid size */}
          <div className="flex items-center gap-1">
            {isScratchpadMode ? (
              <span className="text-xs text-gray-400">
                Free canvas
              </span>
            ) : (
              <>
                <input
                  type="number"
                  min={5}
                  value={gridWidth}
                  onChange={e => setGridWidth(parseInt(e.target.value) || DEFAULT_GRID_SIZE)}
                  className="w-10 px-1 py-0.5 text-xs border border-gray-200 rounded text-center"
                />
                <span className="text-xs text-gray-400">×</span>
                <input
                  type="number"
                  min={5}
                  value={gridHeight}
                  onChange={e => setGridHeight(parseInt(e.target.value) || DEFAULT_GRID_SIZE)}
                  className="w-10 px-1 py-0.5 text-xs border border-gray-200 rounded text-center"
                />
              </>
            )}
            <button
              onClick={isScratchpadMode ? handleClearScratchpadGrid : handleResizeGrid}
              className="px-1.5 py-0.5 text-xs text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded"
            >
              {isScratchpadMode ? 'Clear' : 'Set'}
            </button>
          </div>

          <div className="w-px h-4 bg-gray-200" />

          {/* View mode — side-by-side shows both lists; single uses an in-grid
              A/B toggle so the grid takes half the width. */}
          <div className="flex gap-0.5 p-0.5 bg-gray-100 rounded">
            {([
              { id: 'side-by-side', label: 'Both' },
              { id: 'overlay', label: 'Single' },
            ] as const).map(mode => (
              <button
                key={mode.id}
                onClick={() => setConstructorViewMode(mode.id)}
                className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${constructorViewMode === mode.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-900'
                  }`}
                title={mode.id === 'side-by-side' ? 'Show both lists side-by-side' : 'Show one list at a time (toggle A/B in grid)'}
              >
                {mode.label}
              </button>
            ))}
          </div>

          {/* <div className="w-px h-4 bg-gray-200" /> */}

          {/* Direction toggle */}
          {/* <div className="flex gap-0.5 p-0.5 bg-gray-100 rounded">
            <button
              onClick={() => setPlacementDirection('across')}
              className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${placementDirection === 'across'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-900'
                }`}
            >
              →
            </button>
            <button
              onClick={() => setPlacementDirection('down')}
              className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${placementDirection === 'down'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-900'
                }`}
            >
              ↓
            </button>
          </div> */}

          <div className="flex-1" />

          {/* Assistant panel toggle — re-opens the right-side panel after the
              user has closed it. Hidden when already open (the panel has its
              own close button). */}
          {!assistantOpen && (
            <button
              onClick={() => setAssistantOpen(true)}
              className="px-2 py-0.5 text-xs text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded"
              title="Open assistant panel"
            >
              ◧ Assistant
            </button>
          )}

          {/* Placed count */}
          {isScratchpadMode ? (
            <span className="text-xs text-gray-400">
              {scratchpadGrid?.placements.length ?? 0} scratchpad
            </span>
          ) : constructorGrid.placements.length > 0 && (
            <span className="text-xs text-gray-400">
              {constructorGrid.placements.length} placed
            </span>
          )}

          {enforceSymmetry && (
            <span className="text-xs text-blue-700">
              Symmetry: On
            </span>
          )}

          <button
            onClick={() => setShowQualityChecks(v => !v)}
            disabled={isScratchpadMode}
            className={`px-2 py-0.5 text-xs rounded transition-colors ${
              isScratchpadMode
                ? 'text-gray-300 cursor-not-allowed'
                : showQualityChecks
                ? 'bg-gray-900 text-white'
                : hasQualityIssues
                  ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                  : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
            }`}
            title="Toggle quality checks"
          >
            Checks{hasQualityIssues ? ` (${qualityReport.totalIssues})` : ''}
          </button>

          {/* Zoom controls */}
          <div className="flex items-center gap-0.5">
            <button
              onClick={() => setZoom(Math.max(0.25, zoom - 0.25))}
              className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded"
              title="Zoom out"
            >
              −
            </button>
            <span className="text-xs text-gray-500 w-10 text-center">{Math.round(zoom * 100)}%</span>
            <button
              onClick={() => setZoom(Math.min(3, zoom + 0.25))}
              className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded"
              title="Zoom in"
            >
              +
            </button>
            <button
              onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }) }}
              className="px-1.5 py-0.5 text-xs text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded"
              title="Reset view"
            >
              1:1
            </button>
            <button
              onClick={handleFitToPanel}
              disabled={isScratchpadMode}
              className="px-1.5 py-0.5 text-xs text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
              title={isScratchpadMode ? 'Fit is only available for the active draft grid' : 'Fit grid to panel'}
            >
              Fit
            </button>
          </div>
        </div>

        {/* Context strip — canonical filter chips above the grid. Lifted
            state, shared with the sidebar pair list and the assistant
            suggestions. */}
        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50/40 space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <StatusFilter
              selectedPairingStatuses={pairStatusFilters}
              selectedClueStatuses={pairClueFilters}
              onPairingStatusChange={setPairStatusFilters}
              onClueStatusChange={setPairClueFilters}
              compact
            />
            <span className="text-gray-200">|</span>
            <UsedFilterChip value={usedFilter} onChange={setUsedFilter} />
            <span className="text-gray-200">|</span>
            <RatingFilter state={pairRatingFilter} onChange={setPairRatingFilter} />
          </div>
          <TagFilterRow state={pairTagFilters} onChange={setPairTagFilters} />
        </div>

        {/* Grid display - takes all remaining space */}
        <div ref={gridDisplayRef} className="flex-1 overflow-hidden">
          {isScratchpadMode && scratchpadGrid ? (
            <GridDisplay
              grid={scratchpadGrid}
              viewMode={constructorViewMode}
              selectedCell={scratchpadSelectedCell}
              selectedPlacementIds={scratchpadSelectedPlacementIds}
              previewPlacement={null}
              previewPlacements={previewPlacements}
              zoom={zoom}
              pan={pan}
              listNames={listNames}
              externalDragPlacementId={draggingFromPaletteId}
              externalDragStartPos={dragStartPos}
              onExternalDragEnd={handleExternalDragEnd}
              onSelectCell={(row, col) => {
                setScratchpadSelectedCell({ row, col })
              }}
              onSelectPlacement={handleSelectPlacement}
              onDropOnCell={handleDropOnCell}
              onToggleBlocked={() => {}}
              onMovePlacements={moveScratchpadPlacements}
              onRotatePlacement={handleRotatePlacement}
              onZoomChange={setZoom}
              onPanChange={handlePanChange}
              onDragStateChange={handleDragStateChange}
              onUpdatePlacementLetter={updateScratchpadPlacementLetter}
              onDeselect={() => {
                setScratchpadSelectedCell(null)
                setScratchpadSelectedPlacementIds(new Set())
              }}
              highlightedWords={highlightedWords}
              scratchpadMode
            />
          ) : (
            <GridDisplay
              grid={constructorGrid}
              viewMode={constructorViewMode}
              selectedCell={selectedGridCell}
              selectedPlacementIds={selectedPlacementIds}
              previewPlacement={previewPlacement}
              previewPlacements={previewPlacements}
              zoom={zoom}
              pan={pan}
              placementIssueCounts={qualityReport.placementIssueCounts}
              placementIssueLabels={qualityReport.placementIssueLabels}
              listNames={listNames}
              externalDragPlacementId={draggingFromPaletteId}
              externalDragStartPos={dragStartPos}
              onExternalDragEnd={handleExternalDragEnd}
              onSelectCell={(row, col) => {
                setSelectedGridCell({ row, col })
              }}
              onSelectPlacement={handleSelectPlacement}
              onDropOnCell={handleDropOnCell}
              onToggleBlocked={handleToggleBlocked}
              onMovePlacements={moveGridPlacements}
              onRotatePlacement={handleRotatePlacement}
              onZoomChange={setZoom}
              onPanChange={handlePanChange}
              onResize={(width, height, rowOffset, colOffset) => {
                setGridWidth(width)
                setGridHeight(height)
                resizeConstructorGrid(width, height, rowOffset, colOffset)
              }}
              onDragStateChange={handleDragStateChange}
              onUpdatePlacementLetter={updateGridPlacementLetter}
              onDeselect={() => {
                setSelectedGridCell(null)
                setSelectedPlacementIds(new Set())
              }}
              onSaveTempWord={saveTempWord}
              highlightedWords={highlightedWords}
            />
          )}
        </div>

        {showQualityChecks && !isScratchpadMode && (
          <div className="absolute top-12 right-3 z-40">
            <ConstructorQualityPanel
              grid={constructorGrid}
              pairings={pairings}
              onJumpToCell={(row, col) => {
                setSelectedGridCell({ row, col })
              }}
              onJumpToPlacement={(placementId) => setSelectedPlacementIds(new Set([placementId]))}
            />
          </div>
        )}

        {/* Status bar - always visible to prevent layout shift */}
        <div className={`border-t ${(isScratchpadMode ? scratchpadSelectedPlacementIds.size > 0 : selectedPlacementIds.size > 0) ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center gap-2 px-3 h-8">
          {isScratchpadMode ? (
            scratchpadSelectedPlacementIds.size > 1 ? (
              <>
                <span className="text-xs font-medium text-blue-900">
                  {scratchpadSelectedPlacementIds.size} selected
                </span>
                <button
                  onClick={() => {
                    for (const id of scratchpadSelectedPlacementIds) rotateScratchpadPlacement(id)
                  }}
                  className="px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-100 rounded"
                >
                  Rotate all
                </button>
                <button
                  onClick={() => {
                    removeManyFromScratchpad([...scratchpadSelectedPlacementIds])
                    setScratchpadSelectedPlacementIds(new Set())
                  }}
                  className="px-2 py-0.5 text-xs text-red-600 hover:bg-red-100 rounded"
                >
                  Remove all
                </button>
                <span className="ml-auto text-xs text-gray-400">
                  Shift+arrows or drag any selected pair to move them together
                </span>
              </>
            ) : scratchpadSelectedPlacement ? (
              <>
                <span className="text-xs font-medium text-blue-900">
                  {scratchpadSelectedPlacement.wordAText}/{scratchpadSelectedPlacement.wordBText}
                </span>
                <span className="text-xs text-blue-600">
                  ({scratchpadSelectedPlacement.row}, {scratchpadSelectedPlacement.col}) {scratchpadSelectedPlacement.direction === 'across' ? '→' : '↓'}
                </span>
                {scratchpadSelectedPlacement.pairingId && (
                  <button
                    onClick={() => {
                      setSelectedPairing(scratchpadSelectedPlacement.pairingId)
                      onPairClick?.()
                    }}
                    className="px-1.5 py-0.5 text-xs text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded"
                    title="Open in pair panel"
                  >
                    ↗
                  </button>
                )}
                <button
                  onClick={() => handleRotatePlacement(scratchpadSelectedPlacement.id)}
                  className="px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-100 rounded"
                >
                  Rotate
                </button>
                <button
                  onClick={() => {
                    removeFromScratchpad(scratchpadSelectedPlacement.id)
                    setScratchpadSelectedPlacementIds(new Set())
                  }}
                  className="px-2 py-0.5 text-xs text-red-600 hover:bg-red-100 rounded"
                >
                  Remove
                </button>
                <span className="ml-auto text-xs text-gray-400">
                  Scratchpad placements do not count as used in the sidebars
                </span>
              </>
            ) : (
              <span className="text-xs text-gray-400">
                {scratchpadSelectedCell
                  ? `Scratchpad cell (${scratchpadSelectedCell.row}, ${scratchpadSelectedCell.col})`
                  : 'Scratchpad: drag pairs or words here without changing draft usage'}
              </span>
            )
          ) : selectedPlacementIds.size > 1 ? (
            <>
              <span className="text-xs font-medium text-blue-900">
                {selectedPlacementIds.size} selected
              </span>
              <button
                onClick={() => {
                  for (const id of selectedPlacementIds) rotateGridPlacement(id)
                }}
                className="px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-100 rounded"
              >
                Rotate all
              </button>
              <button
                onClick={() => {
                  if (!constructorGrid) return
                  const selected = constructorGrid.placements.filter(p => selectedPlacementIds.has(p.id))
                  if (selected.length === 0) return
                  const name = window.prompt('Name this fragment:', `Fragment ${new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`)
                  if (name === null) return
                  saveFragment(selected, name)
                }}
                className="px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-100 rounded"
                title="Save the selected placements as a reusable fragment"
              >
                Save as fragment
              </button>
              <button
                onClick={() => {
                  const result = sendPlacementsToScratchpad([...selectedPlacementIds])
                  if (!result.success) {
                    alert(`Couldn't send to scratchpad: ${result.error ?? 'unknown'}`)
                    return
                  }
                  setSelectedPlacementIds(new Set())
                }}
                className="px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-100 rounded"
                title="Move the selected placements off the draft and onto the scratchpad"
              >
                Send to scratchpad
              </button>
              <button
                onClick={() => {
                  removeManyFromGrid([...selectedPlacementIds])
                  setSelectedPlacementIds(new Set())
                }}
                className="px-2 py-0.5 text-xs text-red-600 hover:bg-red-100 rounded"
              >
                Remove all
              </button>
              <span className="ml-auto text-xs text-gray-400">
                Shift+arrows or drag any selected pair to move them together
              </span>
            </>
          ) : onlySelectedPlacementId ? (
            (() => {
              const placementId = onlySelectedPlacementId
              const placement = constructorGrid.placements.find(p => p.id === placementId)
              if (!placement) return <span className="text-xs text-gray-400">No selection</span>

              // Check if words are temp (not saved) and not placeholders
              const wordAIsPlaceholder = placement.wordAText.includes('□')
              const wordBIsPlaceholder = placement.wordBText.includes('□')
              const canSaveA = placement.isTempA && !wordAIsPlaceholder
              const canSaveB = placement.isTempB && !wordBIsPlaceholder

              // Resolve clue from placement, falling back to the linked pairing
              const linkedPairing = placement.pairingId ? pairingsById.get(placement.pairingId) : undefined
              const resolvedClue = linkedPairing?.clue?.text ?? ''

              return (
                <>
                  <span className="text-xs font-medium text-blue-900">
                    {placement.wordAText}/{placement.wordBText}
                  </span>
                  <span className="text-xs text-blue-600">
                    ({placement.row + 1}, {placement.col + 1}) {placement.direction === 'across' ? '→' : '↓'}
                  </span>
                  <input
                    type="text"
                    className="flex-1 mx-1 px-1.5 py-0 text-xs bg-transparent border-b border-blue-200 focus:border-blue-400 focus:outline-none text-blue-800 placeholder-blue-300"
                    placeholder="Clue..."
                    defaultValue={resolvedClue}
                    key={placementId}
                    onBlur={(e) => {
                      const val = e.currentTarget.value.trim()
                      if (placement.pairingId && val !== resolvedClue) {
                        updatePairingClue(placement.pairingId, val, val ? 'draft' : 'draft')
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur()
                      }
                      e.stopPropagation()
                    }}
                  />
                  {placement.pairingId && (
                    <button
                      onClick={() => {
                        setSelectedPairing(placement.pairingId)
                        onPairClick?.()
                      }}
                      className="px-1.5 py-0.5 text-xs text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded"
                      title="Open in pair panel"
                    >
                      ↗
                    </button>
                  )}
                  {/* Priority toggle */}
                  <button
                    onClick={() => {
                      if (placement.priority != null) {
                        setPlacementPriority(placementId, undefined)
                      } else {
                        // Assign next available priority number
                        const usedPriorities = constructorGrid.placements
                          .filter(p => p.priority != null)
                          .map(p => p.priority!)
                        const next = usedPriorities.length === 0 ? 1 : Math.max(...usedPriorities) + 1
                        setPlacementPriority(placementId, next)
                      }
                    }}
                    className={`px-2 py-0.5 text-xs rounded ${
                      placement.priority != null
                        ? 'text-amber-700 bg-amber-100 hover:bg-amber-200'
                        : 'text-gray-500 hover:bg-gray-100'
                    }`}
                    title={placement.priority != null ? `Priority #${placement.priority} (click to remove)` : 'Mark as priority (numbered first in print)'}
                  >
                    {placement.priority != null ? `★ ${placement.priority}` : '☆'}
                  </button>
                  {/* Save buttons for temp words */}
                  {canSaveA && canSaveB ? (
                    <button
                      onClick={() => {
                        saveTempWord(placementId, 'A')
                        saveTempWord(placementId, 'B')
                      }}
                      className="px-2 py-0.5 text-xs text-amber-700 bg-amber-100 hover:bg-amber-200 rounded"
                    >
                      Save pair
                    </button>
                  ) : (
                    <>
                      {canSaveA && (
                        <button
                          onClick={() => saveTempWord(placementId, 'A')}
                          className="px-2 py-0.5 text-xs text-amber-700 bg-amber-100 hover:bg-amber-200 rounded"
                        >
                          Save to {listNames.A}
                        </button>
                      )}
                      {canSaveB && (
                        <button
                          onClick={() => saveTempWord(placementId, 'B')}
                          className="px-2 py-0.5 text-xs text-amber-700 bg-amber-100 hover:bg-amber-200 rounded"
                        >
                          Save to {listNames.B}
                        </button>
                      )}
                    </>
                  )}
                  <button
                    onClick={() => handleRotatePlacement(placementId)}
                    className="px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-100 rounded"
                  >
                    Rotate
                  </button>
                  <button
                    onClick={() => {
                      removeFromGrid(placementId)
                      setSelectedPlacementIds(new Set())
                    }}
                    className="px-2 py-0.5 text-xs text-red-600 hover:bg-red-100 rounded"
                  >
                    Remove
                  </button>
                </>
              )
            })()
          ) : (
            <span className="text-xs text-gray-400">
              {selectedGridCell
                ? `Cell (${selectedGridCell.row + 1}, ${selectedGridCell.col + 1})`
                : 'Click a cell or word to select'}
            </span>
          )}
          </div>
        </div>
      </div>

      {/* Right-side assistant panel. Anchored after the grid; toggleable.
          Phase 1 hosts the pair detail (and crossings as a sub-section);
          Phase 2 will fold suggestions into the empty state and unify
          placements + solutions into one ranked body. */}
      <AssistantPanel
        open={assistantOpen}
        onClose={() => setAssistantOpen(false)}
        chromeless={selectedPairing !== null}
        body={
          selectedPairing ? (
            pairCrossingsOpen ? (
              <CrossingsPanel
                pairing={selectedPairing}
                onClose={() => setPairCrossingsOpen(false)}
              />
            ) : (
              <PairDetailColumn
                pairing={selectedPairing}
                grid={activeGrid}
                onClose={() => setSelectedPairing(null)}
                onShowWord={(wordId) => {
                  // Selects the word in the store; users navigate to the Words
                  // page via the top nav to view it.
                  setSelectedWord(wordId)
                }}
                onShowCrossings={() => setPairCrossingsOpen(o => !o)}
                crossingsOpen={pairCrossingsOpen}
                filters={{ statusFilters: pairStatusFilters, clueFilters: pairClueFilters }}
                onPreviewChange={setPreviewPlacement}
                onPreviewPlacementsChange={setPreviewPlacements}
                onPlacePair={(_wordAText, _wordBText, row, col, direction) => {
                  handlePlacePairFromSuggestion(selectedPairing.id, row, col, direction)
                }}
              />
            )
          ) : (
            <CellSuggestionsBody
              grid={activeGrid}
              selectedCell={activeSelectedCell}
              fittingPairs={fittingPairsForCell}
              fittingSingleWords={fittingSingleWordsForCell}
              statusFilters={pairStatusFilters}
              clueFilters={pairClueFilters}
              tagFilters={pairTagFilters}
              usedTagsInGrid={usedTagsInGrid}
              usedFilter={usedFilter}
              placedPairingIds={placedInActiveDraftPairingIds}
              ratingFilter={pairRatingFilter}
              onPlaceSuggestion={(wordAText, wordBText, row, col, direction, isTempA, isTempB) => {
                if (isTempA || isTempB) {
                  const origin = getActiveCanvasOrigin(row, col)
                  placePlacementOnActiveCanvas({
                    pairingId: null,
                    wordAText,
                    wordBText,
                    row: origin.row,
                    col: origin.col,
                    direction,
                    isTempA,
                    isTempB,
                  })
                } else {
                  handlePlacePairWithWords(wordAText, wordBText, row, col, direction)
                }
                setPreviewPlacement(null)
              }}
              onPreviewChange={setPreviewPlacement}
              onPairHover={(wordTexts) => {
                if (wordTexts) {
                  setHighlightedWords(new Set(wordTexts.map(getLettersFromText)))
                } else {
                  setHighlightedWords(new Set())
                }
              }}
            />
          )
        }
      />

      {/* Manual entry modal */}
      <ManualEntryModal
        isOpen={showManualEntry}
        onClose={() => setShowManualEntry(false)}
        onSave={handleManualEntrySave}
      />
    </div>
  )
}
